import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "./DownloadAppPage.css";
import logo from "../../components/assets/logo1.png";
import openNotification from "components/utils/CustomNotification";
import { FiDownload } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useConsent } from "components/utils/ConsentContext"

const AppDownloadPage = () => {
  const [apkInfo, setApkInfo] = useState<any>(null);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const sessionUid = localStorage.getItem("session_uid") ?? "";
  const userUid = localStorage.getItem("user_uid") ?? "";
  const orgUid = localStorage.getItem("org_uid") ?? "";
  const release_type = localStorage.getItem("release_group") || "stable";
  const [downloadComplete, setDownloadComplete] = useState(false);
  const { mappConsent, isLoading: consentLoading, refreshConsent } = useConsent();

  // Add refs to prevent multiple API calls

  const hasNavigated = useRef(false);

  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!sessionUid || !userUid || !orgUid) {
      localStorage.clear();
      navigate("/login", { replace: true });
    }
  }, [sessionUid, userUid, orgUid, navigate]);

  useEffect(() => {
    // Always refresh consent when visiting this page
    refreshConsent();
  }, [refreshConsent]);

  useEffect(() => {
    if (!consentLoading) {
      if (mappConsent) {
        navigate("/home", { replace: true });
      } else {
        setLoading(false);
      }
    }
  }, [consentLoading, mappConsent, navigate]);

  const handleConsentCheck = async () => {
    setLoading(true);
    await refreshConsent();
    // The useEffect above will handle navigation
  };

  useEffect(() => {
    const handleBackButton = async () => {
      try {
        await axios.post(`${beAddress}/api/logout`, null, {
          headers: {
            "user-uid": userUid,
          }, withCredentials: true,
        });
      } catch (err) {
        console.error("Logout failed on back navigation:", err);
      } finally {
        localStorage.clear();
        navigate("/login", { replace: true });
      }
    };

    window.history.pushState(null, "", window.location.href);

    const onPopState = () => {
      handleBackButton();
    };

    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [navigate, beAddress, userUid, sessionUid]);

  useEffect(() => {
    if (window.history.state === null) {
      window.history.pushState({ page: "cofit" }, "", window.location.href);
    }
  }, []);

  useEffect(() => {
    const fetchApkInfo = async () => {
      // Only fetch if we're staying on this page (not navigating away)
      if (hasNavigated.current || !sessionUid || !userUid || !orgUid) {
        return;
      }

      try {
        const response = await axios.get(`${beAddress}/api/v1/get-apk-info`, {
          headers: {
            accept: 'application/json',
            'user-uid': userUid,
          }, withCredentials: true,
          params: {
            org_uid: orgUid,
            latest: true,
            releasetype: release_type,
          },
        });

        if (response.status === 200 && response.data.status === 'Success') {
          setApkInfo(response.data.data[0]);
        } else {
          console.warn("APK Info fetch failed:", response.data.message);
        }
      } catch (err) {
        console.error("Error fetching APK info:", err);
      }
    };

    // Only fetch APK info if we're not loading and haven't navigated
    if (!loading && !hasNavigated.current && sessionUid && userUid && orgUid) {
      fetchApkInfo();
    }
  }, [beAddress, sessionUid, userUid, orgUid, loading]);

  const handleApkDownload = async () => {
    if (!sessionUid || !userUid || !orgUid || isDownloading) return;

    if (!apkInfo) {
      openNotification({
        type: "info",
        message: "No APK available for download.",
      });
      return;
    }


    setIsDownloading(true);
    const { apkfilename, release_type, version_code } = apkInfo;
    const params = `org_uid=${orgUid}&releasetype=${release_type}&apkfilename=${apkfilename}&version_code=${version_code}`;
    setDownloadProgress(0);

    try {
      const response = await axios.get(`${beAddress}/api/v1/apk/apk-download?${params}`, {
        headers: {
          'user-uid': userUid,
        }, withCredentials: true,
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setDownloadProgress(percent);
        }
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.android.package-archive',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = apkfilename + '.apk';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDownloadComplete(true);

    } catch (error) {
      openNotification({ type: "error", message: "App Download Error" });
    }
    finally {
      setDownloadProgress(null);
      setIsDownloading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const response = await axios.post(`${beAddress}/api/logout`, null, {
        headers: {
          "user-uid": userUid,
        }, withCredentials: true,
      });

      if (response.data?.status === "Success") {
        localStorage.clear();
        navigate("/login", { replace: true });
      } else {
        openNotification({
          type: "error",
          message: response.data?.message || "Logout failed",
        });
      }
    } catch (err) {
      console.error("Logout error", err);
      openNotification({ type: "error", message: "Logout request failed" });
    }
  };

  // Show loading state while checking profile
  if (loading) {
    return (
      <div className="appdownload-wrapper">
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ position: "absolute", top: "20px", right: "20px", zIndex: 1000 }}>
        <button className="logout-arrow-btn" onClick={handleLogout} title="Logout">
          Logout ⟶
        </button>
      </div>

      <div className="appdownload-wrapper">
        <div className="appdownload-branding">
          <span className="appdownload-brand-text">PulseRank</span>
          <img src={logo} alt="PulseRank Logo" className="appdownload-brand-logo" />
        </div>
        <p className="appdownload-subtitle" style={{ margin: "0px" }}>
          Corporate Wellness Redefined.
        </p>

        <div className="download-layout-container">
          <div className="download-left-text">
            <h1 className="appdownload-title1">Start Your PulseRank Journey</h1>
            <p className="appdownload-subtitle1">
              Download the PulseRank app and track your fitness in sync with your organization.
            </p>
            {(isDownloading || downloadComplete) && (
              <p
                style={{
                  marginTop: "2px",
                  marginBottom: "5px",
                  fontSize: "14px",
                  textAlign: "center",
                  color: downloadComplete ? "green" : "#cc0000",
                  fontWeight: 500,
                }}
              >
                {downloadComplete
                  ? "✅ Download complete! Install the app and register yourself!"
                  : "🚫 Do not navigate or refresh while download is in progress."}
              </p>
            )}
          </div>

          <div className="download-btn-wrapper">
            <button
              className="download-btn"
              onClick={downloadComplete ? handleLogout : handleApkDownload}
              disabled={isDownloading}
            >
              <div
                className="progress-fill-bg"
                style={{ width: `${downloadProgress ?? 0}%` }}
              ></div>
              <span className="download-btn-text">
                {isDownloading
                  ? "Downloading please wait..."
                  : downloadComplete
                    ? "Logout"
                    : "Download APK"}
              </span>
            </button>
          </div>
        </div>

        <p className="appdownload-subtitle" style={{ marginTop: "10px" }}>
        </p>
        <p className="appdownload-subtitle">
          Open the App, Accept the <strong>Terms and Conditions</strong> and provide required <strong>Consent</strong> 0nly then the Web Login will be enabled
        </p>
        <div style={{ marginTop: "20px", textAlign: "center" }}>
        </div>
        <div style={{ marginTop: "30px", textAlign: "center" }}>
          <span>Need help? </span>
          <a
            href="/COFIT_INSTALLATION_GUIDE.pdf"
            download
            style={{ color: "#007bff", textDecoration: "underline", cursor: "pointer" }}
          >
            Click here and download the user guide
          </a>
        </div>
      </div>
    </>
  );
};

export default AppDownloadPage;
